/**
 * Access/refresh session helpers (15m access, 24h absolute session).
 * Refresh hits Node trade-data-server: POST {serverUrl}/auth/refresh
 */
import axios from 'axios';
import { API_ENDPOINT_AUTH_REFRESH } from '../data/Endpoints-API';

export const REFRESH_TOKEN_KEY = 'refreshToken';
export const SESSION_EXP_KEY = 'sessionExp';
export const SESSION_TOKEN_REFRESHED_EVENT = 'sessionTokenRefreshed';

/** Refresh a bit before access TTL (15m) ends. */
const REFRESH_INTERVAL_MS = 14 * 60 * 1000;

let refreshTimerId = null;
let refreshInFlight = null;
/** Bumped on logout/stop so an in-flight refresh cannot write tokens back. */
let sessionEpoch = 0;

export function persistSessionTokens(response) {
  if (!response?.token) return;
  localStorage.setItem('token', response.token);
  if (response.refreshToken) {
    localStorage.setItem(REFRESH_TOKEN_KEY, response.refreshToken);
  }
  if (response.sessionExp != null) {
    localStorage.setItem(SESSION_EXP_KEY, String(response.sessionExp));
  }
  if (response.accountType != null) {
    localStorage.setItem('accountType', String(response.accountType));
  }
}

export function clearSessionTokens() {
  localStorage.removeItem('token');
  localStorage.removeItem(REFRESH_TOKEN_KEY);
  localStorage.removeItem(SESSION_EXP_KEY);
}

export function getSessionExp() {
  const raw = localStorage.getItem(SESSION_EXP_KEY);
  if (!raw) return null;
  const n = Number(raw);
  return Number.isFinite(n) ? n : null;
}

export function isSessionAbsolutelyExpired() {
  const exp = getSessionExp();
  if (exp == null) return false;
  return Math.floor(Date.now() / 1000) >= exp;
}

/**
 * Call Node /auth/refresh. Uses raw axios to avoid APIMiddleware 401 recursion.
 * @returns {Promise<object>} refresh response body
 */
export async function refreshAccessToken() {
  if (refreshInFlight) return refreshInFlight;

  const epoch = sessionEpoch;
  refreshInFlight = (async () => {
    if (isSessionAbsolutelyExpired()) {
      const err = new Error('session_expired');
      err.code = 'session_expired';
      throw err;
    }

    const refreshToken = localStorage.getItem(REFRESH_TOKEN_KEY);
    if (!refreshToken) {
      const err = new Error('invalid_refresh');
      err.code = 'invalid_refresh';
      throw err;
    }

    const url = API_ENDPOINT_AUTH_REFRESH();
    const { data } = await axios.post(
      url,
      { refreshToken },
      {
        headers: { 'Content-Type': 'application/json' },
        withCredentials: true,
      }
    );

    if (!data?.token) {
      const err = new Error('invalid_refresh');
      err.code = 'invalid_refresh';
      throw err;
    }

    if (epoch !== sessionEpoch) {
      const err = new Error('cancelled');
      err.code = 'cancelled';
      throw err;
    }

    persistSessionTokens(data);
    if (epoch !== sessionEpoch) {
      clearSessionTokens();
      const err = new Error('cancelled');
      err.code = 'cancelled';
      throw err;
    }
    window.dispatchEvent(
      new CustomEvent(SESSION_TOKEN_REFRESHED_EVENT, {
        detail: { token: data.token, sessionExp: data.sessionExp },
      })
    );
    return data;
  })();

  try {
    return await refreshInFlight;
  } catch (error) {
    const code = error?.response?.data?.error || error?.code;
    if (code === 'session_expired' || code === 'invalid_refresh' || code === 'session_revoked') {
      const err = new Error(code);
      err.code = code;
      throw err;
    }
    throw error;
  } finally {
    refreshInFlight = null;
  }
}

export function stopSessionRefreshScheduler() {
  sessionEpoch += 1;
  if (refreshTimerId != null) {
    clearInterval(refreshTimerId);
    refreshTimerId = null;
  }
}

/**
 * Proactive silent refresh every ~14 minutes until sessionExp.
 * Returns a Promise for the immediate first tick (await before WS connect on restore).
 */
export function startSessionRefreshScheduler() {
  stopSessionRefreshScheduler();

  if (!localStorage.getItem(REFRESH_TOKEN_KEY)) return Promise.resolve(true);
  if (isSessionAbsolutelyExpired()) {
    window.dispatchEvent(new CustomEvent('sessionExpired'));
    return Promise.resolve(false);
  }

  const tick = async () => {
    if (!localStorage.getItem(REFRESH_TOKEN_KEY)) {
      stopSessionRefreshScheduler();
      return true;
    }
    if (isSessionAbsolutelyExpired()) {
      stopSessionRefreshScheduler();
      window.dispatchEvent(new CustomEvent('sessionExpired'));
      return false;
    }
    try {
      await refreshAccessToken();
      return true;
    } catch (e) {
      if (e?.code === 'session_expired' || e?.code === 'invalid_refresh' || e?.code === 'session_revoked') {
        stopSessionRefreshScheduler();
        window.dispatchEvent(
          new CustomEvent('sessionExpired', { detail: { reason: e.code } })
        );
        return false;
      }
      // Transient network errors: keep timer; next tick or 401 interceptor retries.
      return true;
    }
  };

  // Run once immediately so a cold restore with an expired access JWT
  // refreshes before API/WS callers need a new token (no WS reconnect-on-refresh).
  const firstTick = tick();
  refreshTimerId = setInterval(tick, REFRESH_INTERVAL_MS);
  return firstTick;
}
